import type { ExtraInclusion, SourceRoom } from './types';

const sqmToSqft = (sqm: number) => {
  return Math.round(sqm * 10.7639);
};

const generateRoomSizeText = (room: SourceRoom): string => {
  const roomSizeSQM = room?.roomSize;
  const roomSizeFT = room?.roomSize ? sqmToSqft(room?.roomSize) : undefined;

  const roomSizeText = roomSizeSQM ? `${roomSizeSQM} m² / ${roomSizeFT} ft²` : 'N/A';
  return roomSizeText;
};

const generateOccupancyText = (room: SourceRoom) => {
  const occupancyFromSourceContentMaxAdults = room?.occupancy?.maxAllowed?.adults ?? 'N/A';
  const occupancyFromSourceContentMaxChildren = room?.occupancy?.maxAllowed?.children ?? 'N/A';
  const occupancyFromSourceContentMaxTotal = room?.occupancy?.maxAllowed?.total ?? 'N/A';

  const occupancyAdultAge = room?.occupancy?.ageCategories?.adult?.minimumAge;
  const occupancyChildAgeAge = room?.occupancy?.ageCategories?.child?.minimumAge;

  const occupancyAge = occupancyAdultAge
    ? `Min adult: ${occupancyAdultAge}, Min child: ${occupancyChildAgeAge ?? 'N/A'}`
    : 'N/A';

  const occupancyAllowed = `Adults: ${occupancyFromSourceContentMaxAdults}, Children: ${occupancyFromSourceContentMaxChildren}, Total: ${occupancyFromSourceContentMaxTotal}`;

  return { occupancyAge, occupancyAllowed };
};

const generateViewsText = (room: SourceRoom) => {
  const description = room?.description ?? 'N/A';
  const doesDescriptionContainView = description.toLowerCase().includes('view');
  const viewsArray = room?.views
    ? Array.isArray(room?.views)
      ? room?.views
      : Object.values(room?.views).map((v: any) => v.name)
    : undefined;
  const views = viewsArray?.join(' ') || (doesDescriptionContainView ? 'Check description for view info' : 'N/A');
  return views;
};

const getUniqueExtraInclusions = (rooms: Array<Pick<SourceRoom, 'extraInclusions'>>): Array<ExtraInclusion> => {
  const seen = new Set<string>();
  const unique: Array<ExtraInclusion> = [];
  for (const room of rooms) {
    for (const inclusion of room.extraInclusions ?? []) {
      if (!seen.has(inclusion.code)) {
        seen.add(inclusion.code);
        unique.push(inclusion);
      }
    }
  }
  return unique;
};

const matchesSourceRoomSearch = (room: SourceRoom, normalizedSearchTerm: string): boolean => {
  if (!normalizedSearchTerm) {
    return true;
  }

  return (
    room.name.toLowerCase().includes(normalizedSearchTerm) ||
    room.views?.some((view) => view.toLowerCase().includes(normalizedSearchTerm)) ||
    room.amenities?.some((amenity) => amenity.toLowerCase().includes(normalizedSearchTerm)) ||
    room.bedGroups?.some((bedGroup) => bedGroup.description.toLowerCase().includes(normalizedSearchTerm)) ||
    room.extraInclusions?.some((inclusion) => inclusion.label.toLowerCase().includes(normalizedSearchTerm)) ||
    room.externalId.toLowerCase().includes(normalizedSearchTerm) ||
    room.description.toLowerCase().includes(normalizedSearchTerm)
  );
};

export {
  generateRoomSizeText,
  generateOccupancyText,
  generateViewsText,
  getUniqueExtraInclusions,
  matchesSourceRoomSearch,
};
